import { useState, useEffect, useRef } from 'react';
import { ClientData } from '../types';
import { UseClientDataResult } from '../types';
import { ClientService } from '../services/clientService';

export function useClientData(): UseClientDataResult {
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    let isMounted = true;

    // Prevent duplicate fetches
    if (fetchedRef.current) {
      return;
    }
    fetchedRef.current = true;

    const fetchClientData = async () => {
      // Wait for auth state to resolve so guestPrefix() returns the correct value
      if (!window.__USER_FETCHED__) {
        await new Promise<void>((resolve) => {
          const interval = setInterval(() => {
            if (window.__USER_FETCHED__) {
              clearInterval(interval);
              resolve();
            }
          }, 50);
        });
      }

      if (!isMounted) return;

      try {
        if (isMounted) {
          setLoading(true);
          setError(null);
        }
        const fetchedClientData = await ClientService.getClientData();
        if (isMounted) {
          setClientData(fetchedClientData);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load client data';
        if (isMounted) {
          setError(errorMessage);
        }
        console.error('Error fetching client data:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchClientData();

    return () => {
      isMounted = false;
    };
  }, []);

  return {
    clientData,
    loading,
    error,
  };
}
